'use client';

import scrollToView from '@ria-client/hooks/dom/useScrollToView';
import { usePageAction } from '@ria-client/hooks/usePageAction';
import { useNWAElementImpression } from '@ria-client/segment/hooks/useNWAElementImpression';
import type { StructuredContentDocument } from '@ria-client/supergraph/structuredContent/types';
import { Dialog, DialogContent, DialogTitle } from '@ria-client/tw-shadcn/components/dialog';
import { cn } from 'cn';
import { ChevronDown } from 'lucide-react';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link } from '../layout/link';
import {
  COMPONENT_ID_DESKTOP,
  COMPONENT_ID_MOBILE,
  getChildrenHeadings,
  type TableOfContentsHeadingProps,
} from './utils';

export interface TableOfContentsProps {
  staticHeadings?: TableOfContentsHeadingProps[];
  sccProps?: StructuredContentDocument;
  children: ReactNode;
  includeMethodology?: boolean;
  includeFAQ?: boolean;
  headingLevel?: number;
  recapListTitle?: string;
  includeComparisonTable?: boolean;
}

interface TableOfContentsLinksProps {
  headings: TableOfContentsHeadingProps[];
  activeId: string;
  offset: number;
  onNavigate: (event: MouseEvent<HTMLAnchorElement>, offset: number) => void;
}

function TableOfContentsLinks({
  headings,
  activeId,
  offset,
  onNavigate,
}: TableOfContentsLinksProps) {
  return headings.map(heading => (
    <li key={heading.id} className="list-none">
      <Link
        href={`#${heading.id}`}
        onClick={event => onNavigate(event, offset)}
        className={cn(
          'flex border-l-4 border-transparent py-2 pr-2 pl-4 text-foreground no-underline hover:border-primary hover:bg-muted hover:no-underline focus-visible:border-primary focus-visible:bg-secondary',
          activeId === heading.id && 'border-primary bg-secondary',
        )}
      >
        <SectionName bold={activeId === heading.id}>{heading.header}</SectionName>
      </Link>
    </li>
  ));
}

// Legacy SectionName: text/textBold variants (300/500, 150%) forced to 14px.
function SectionName({ bold, children }: { bold: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-block font-sans text-[14px] leading-[1.5]',
        bold ? 'px-0 font-medium' : 'px-[2px] font-light',
      )}
    >
      {children}
    </span>
  );
}

export function TableOfContents({
  sccProps,
  staticHeadings = [],
  children,
  includeMethodology = false,
  includeFAQ = false,
  headingLevel = 2,
  recapListTitle,
  includeComparisonTable = false,
}: TableOfContentsProps) {
  const headings = useMemo(
    () => [
      ...staticHeadings,
      ...(sccProps ? getChildrenHeadings(sccProps) : []),
      ...(includeComparisonTable
        ? [{ id: 'cross-sell-module', header: 'You may also like these' }]
        : []),
      ...(includeMethodology ? [{ id: 'methodology', header: 'Methodology' }] : []),
      ...(recapListTitle ? [{ id: 'recap', header: recapListTitle }] : []),
      ...(includeFAQ ? [{ id: 'faq', header: 'Frequently Asked Questions' }] : []),
    ],
    [
      includeComparisonTable,
      includeFAQ,
      includeMethodology,
      recapListTitle,
      sccProps,
      staticHeadings,
    ],
  );
  const [activeId, setActiveId] = useState(headings[0]?.id ?? '');
  const [mobileOpen, setMobileOpen] = useState(false);
  const trackingPaused = useRef(false);
  const {
    ref: impressionRef,
    impressionId,
    ensureViewed,
  } = useNWAElementImpression({
    component: 'toc_interacted',
    sectionName: 'table_of_contents',
  });
  const firePageAction = usePageAction({
    actionName: 'toc_interacted',
    actionSection: 'table_of_contents',
    actionType: 'navigate',
    parentEventId: impressionId,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (trackingPaused.current) return;

        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const id = visible?.target.querySelector(`h${headingLevel}`)?.id;
        if (id) setActiveId(id);
      },
      { threshold: Array.from({ length: 11 }, (_, index) => index * 0.1) },
    );

    headings.forEach(({ id }) => {
      let section: HTMLElement | null = document.getElementById(id);
      for (let index = 0; index < 2; index += 1) section = section?.parentElement ?? null;
      if (section) observer.observe(section);
    });

    trackingPaused.current = false;
    return () => observer.disconnect();
  }, [headingLevel, headings]);

  const navigateToHeading = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>, offset: number) => {
      event.preventDefault();
      const id = event.currentTarget.hash.slice(1);
      const header = event.currentTarget.textContent ?? '';
      trackingPaused.current = true;
      setMobileOpen(false);
      startTransition(() => setActiveId(id));
      ensureViewed();
      firePageAction({
        action_input: `${headings.findIndex(heading => heading.id === id)}`,
        page_url: window.location.href,
        link_copy: header,
        additional_attributes: { toc_section: header },
      });
      await scrollToView(id, 'smooth', offset, true);
      trackingPaused.current = false;
    },
    [ensureViewed, firePageAction, headings],
  );

  return (
    <div ref={impressionRef} className="relative flex flex-col gap-11 md:flex-row md:gap-16">
      <aside className="hidden shrink-0 md:sticky md:top-0 md:z-[2] md:block md:w-[calc(25%-48px)] md:self-start">
        <nav
          id={COMPONENT_ID_DESKTOP}
          aria-label="table of contents"
          className="max-h-screen w-full overflow-y-auto"
        >
          <div className="mb-2 border-b-4 border-primary p-2 pl-4">
            {/* 0.05rem/450: legacy TableOfContents Title. */}
            <h2 id="on-this-page" className="font-sans text-base font-[450] tracking-[0.05rem]">
              ON THIS PAGE
            </h2>
          </div>
          <ol aria-labelledby="on-this-page" className="m-0 p-0">
            <TableOfContentsLinks
              headings={headings}
              activeId={activeId}
              offset={30}
              onNavigate={navigateToHeading}
            />
          </ol>
        </nav>
      </aside>
      <div
        id={COMPONENT_ID_MOBILE}
        className="sticky top-0 z-[2] -ml-4 w-screen border-b border-border bg-background shadow-[0_8px_8px_0_#64666a26] md:hidden"
      >
        <button
          type="button"
          onClick={() => {
            startTransition(() => setMobileOpen(true));
            const activeElement = document.activeElement;
            if (activeElement instanceof HTMLElement) activeElement.blur();
          }}
          className="w-full pt-4 text-left outline-none hover:bg-muted focus-visible:focus-ring"
        >
          <span className="mb-2 flex items-center justify-between px-4">
            {/* 0.05rem/450: legacy TableOfContents Title. */}
            <span className="font-sans text-base font-[450] tracking-[0.05rem]">ON THIS PAGE</span>
            <ChevronDown aria-hidden="true" className="size-6" />
          </span>
          {headings.find(heading => heading.id === activeId) && (
            <span className="flex border-l-4 border-primary bg-secondary px-4 py-2 text-left">
              <SectionName bold>
                {headings.find(heading => heading.id === activeId)?.header}
              </SectionName>
            </span>
          )}
        </button>
        <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
          <DialogContent className="top-auto bottom-0 h-4/5 rounded-t-[10px] p-4 sm:top-auto sm:right-0 sm:bottom-0 sm:left-0 sm:h-[55vh] sm:max-w-none sm:translate-x-0 sm:translate-y-0 sm:rounded-t-[10px]">
            <nav aria-label="table of contents" className="flex h-full min-h-0 flex-col">
              <div className="border-b-4 border-primary p-4 pr-12">
                {/* 0.05rem/450: legacy TableOfContents Title. */}
                <DialogTitle
                  id="on-this-page-mobile"
                  className="font-sans text-base font-[450] tracking-[0.05rem]"
                >
                  ON THIS PAGE
                </DialogTitle>
              </div>
              <ol aria-labelledby="on-this-page-mobile" className="m-0 overflow-y-auto p-2">
                <TableOfContentsLinks
                  headings={headings}
                  activeId={activeId}
                  offset={120}
                  onNavigate={navigateToHeading}
                />
              </ol>
            </nav>
          </DialogContent>
        </Dialog>
      </div>
      <div className="min-w-0 flex-1 md:w-3/4">{children}</div>
    </div>
  );
}
